import type { ValidationIssue } from '@customerio/dryad';
import { isRecord } from '@/utils/objects';
import type { AttributeFilter } from './wire-types';
import {
  isOneOfOperator,
  isOperatorInFamilies,
  familyOfOperator,
  operatorArity,
  operatorsInFamily,
  timestampVariantOf,
  type OperatorFamily,
} from './operators';

/**
 * A single attribute filter as the condition rows edit it. `operator` uses
 * the UI convention where negation is a '!' prefix ('!eq'); the wire format
 * instead wraps the filter in `{ not: ... }`. `value2` is set only for the
 * between operators (bw, timestamp_bw). Unary operators carry no value —
 * the serializers below drop it, reproducing the wire the Ember app writes
 * for the same edit (both apps serve /profiles while `profiles-migration`
 * rolls out, so the bytes must match).
 */
export type AttributeFilterState = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
};

export type OperatorOption = { value: string; label: string; isDisabled?: boolean };
export type OperatorGroup = { label: string; options: OperatorOption[] };

/**
 * The backend serves one_of as a union of per-attribute-value indexes and
 * keeps no per-value index for cio_id (equality on it is answered by a direct
 * internal-id lookup instead), so it rejects one_of on cio_id at query time.
 * created_in_cio_at takes only timestamp operators.
 */
const ONE_OF_DISALLOWED_FIELDS = new Set<string>(['cio_id', '_created_in_customerio_at']);

/**
 * Which families each dropdown group shows, in display order — both the order
 * of the groups and the order of families within a group. This is the one
 * place the mapping lives; a surface declares families and the groups follow,
 * so the dropdown can't drift from the plugin's claim.
 */
const GROUP_FAMILIES = [
  { label: 'String or Number', families: ['equality', 'inequality', 'oneOf', 'existence'] },
  { label: 'String', families: ['string'] },
  { label: 'Email', families: ['email'] },
  { label: 'Unix Timestamp', families: ['dateExistence', 'dateInequality', 'extraDateInequality'] },
] as const satisfies readonly { label: string; families: readonly OperatorFamily[] }[];

/**
 * The families a dropdown can actually show — those `GROUP_FAMILIES` places in
 * a group. Deliberately narrower than `OperatorFamily`: `array` has no ported
 * sub-editor. Surfaces declare `readonly GroupableFamily[]`, so declaring a
 * family with nowhere to render is a compile error rather than an option that
 * silently vanishes from the dropdown while the plugin's `parse` still claims
 * it.
 */
export type GroupableFamily = (typeof GROUP_FAMILIES)[number]['families'][number];

function optionsForFamily(family: GroupableFamily, field: string): OperatorOption[] {
  if (family === 'oneOf') {
    const isDisabled = ONE_OF_DISALLOWED_FIELDS.has(field.trim());
    return operatorsInFamily('oneOf').map((operator) => ({ ...operator, isDisabled }));
  }
  if (family === 'email') {
    // The email operators are only usable on the literal 'email' field but
    // stay visible. Untrimmed on purpose — the raw attribute name is compared,
    // so '  email  ' is a different attribute.
    return operatorsInFamily('email').map((operator) => ({ ...operator, isDisabled: field !== 'email' }));
  }
  if (family === 'dateInequality') {
    // Only the relative pair is pickable: the absolute variants are reached
    // through the row's "a relative date / a specific date" select, which
    // rewrites the operator between the paired forms.
    return operatorsInFamily(family).filter((operator) => timestampVariantOf(operator.value) === 'relative');
  }
  return [...operatorsInFamily(family)];
}

/**
 * Derives the grouped operator dropdown from a surface's declared families.
 * Groups with no declared families are dropped rather than rendered empty, so
 * the returned list is exactly what the surface offers.
 */
export function groupsForFamilies(
  declared: readonly GroupableFamily[],
  field: string,
  allowOneOfOperators = false,
): OperatorGroup[] {
  const allowed = new Set<GroupableFamily>(declared);
  if (!allowOneOfOperators) {
    allowed.delete('oneOf');
  }
  const groups: OperatorGroup[] = [];
  for (const { label, families } of GROUP_FAMILIES) {
    const options = families
      .filter((family) => allowed.has(family))
      .flatMap((family) => optionsForFamily(family, field));
    if (options.length > 0) {
      groups.push({ label, options });
    }
  }
  return groups;
}

/**
 * An operator a surface hasn't declared is declined rather than parsed into a
 * row whose dropdown can't offer it: dryad keeps the condition as a read-only
 * error node that re-serializes its raw wire unchanged. Gated on the same
 * declared families the dropdown derives from (via groupsForFamilies), so the
 * two readers cannot disagree.
 */
function isSupportedOperator(
  operator: string,
  families: readonly GroupableFamily[],
  allowOneOfOperators: boolean,
): boolean {
  if (!allowOneOfOperators && isOneOfOperator(operator)) {
    return false;
  }
  return isOperatorInFamilies(operator, new Set(families));
}

/**
 * Unary operators (exists, timestamp, email_valid and negations) carry no
 * value on the wire or in the UI. Arity is declared per operator, and a
 * negation inherits its base's.
 */
export function isUnaryFilterOperator(operator: string): boolean {
  return operatorArity(operator) === 0;
}

/** Between operators (bw, timestamp_bw) need value and value2. */
export function isTwoValueFilterOperator(operator: string): boolean {
  return operatorArity(operator) === 2;
}

// A switch must drop existing values when either operator is unary — into one
// because the new operator takes no value, out of one because the only value a
// unary operator can hold came off the wire and the editor never shows it.
// Also when crossing a boundary where the value semantics change: into or out
// of the range picker (timestamp_bw, epoch pair), and into or out of the
// timestamp inequalities (signed seconds offset or epoch, depending on
// variant). Within the inequality family the value carries — after/before read
// it identically. Shared by every operator-change handler so the rule can't
// drift between them.
// fallow-ignore-next-line complexity -- a flat sequence of boundary guards; splitting hurts readability
export function clearsValues(nextOperator: string, prevOperator: string): boolean {
  if (isUnaryFilterOperator(nextOperator) || isUnaryFilterOperator(prevOperator)) {
    return true;
  }
  if (nextOperator === 'timestamp_bw' || prevOperator === 'timestamp_bw') {
    return true;
  }
  return (timestampVariantOf(nextOperator) == null) !== (timestampVariantOf(prevOperator) == null);
}

function isEmailOperator(operator: string): boolean {
  return familyOfOperator(operator) === 'email';
}

export type WireAttribute = {
  field?: unknown;
  operator?: unknown;
  value?: unknown;
  value2?: unknown;
  inverse?: unknown;
};

function keyedAttributeProperty(json: unknown, key: string): WireAttribute | null {
  if (!isRecord(json)) {
    return null;
  }
  const attribute = json[key];
  return isRecord(attribute) ? attribute : null;
}

/**
 * Unwraps `{ <key>: {...} }` or `{ not: { <key>: {...} } }` into the inner
 * attribute payload plus negation flag, or null if the node is any other
 * shape (segment filters, and/or groups, nested nots...). Shared by the
 * attribute-shaped envelopes (`attribute`, `device_attribute`).
 */
export function unwrapAttributeEnvelope(
  json: unknown,
  key: string,
): { attribute: WireAttribute; negated: boolean } | null {
  const direct = keyedAttributeProperty(json, key);
  if (direct != null) {
    return { attribute: direct, negated: false };
  }
  const negated = isRecord(json) ? keyedAttributeProperty(json.not, key) : null;
  if (negated != null) {
    return { attribute: negated, negated: true };
  }
  return null;
}

function uiOperator(attribute: WireAttribute, negated: boolean): string {
  const operator = String(attribute.operator ?? '');
  return negated ? `!${operator}` : operator;
}

/**
 * The shape checks every attribute-shaped envelope shares: unwraps the keyed
 * node, requires string field/operator, and declines the legacy `inverse`
 * boolean spelling — the backend's proto parser accepts negation as either the
 * `{ not: ... }` wrapper or an `inverse` flag inside the block, but row state
 * models only the wrapper form (as the '!' operator prefix), so a leaf flagged
 * `inverse: true` rides the error-node passthrough with the key intact instead
 * of losing it on save. Returns the payload plus the '!'-prefixed UI operator.
 */
export function unwrapSupportableAttributeEnvelope(
  json: unknown,
  key: string,
): { attribute: WireAttribute; operator: string } | null {
  const unwrapped = unwrapAttributeEnvelope(json, key);
  if (unwrapped == null) {
    return null;
  }
  const { attribute, negated } = unwrapped;
  if (typeof attribute.field !== 'string' || typeof attribute.operator !== 'string' || attribute.inverse === true) {
    return null;
  }
  return { attribute, operator: uiOperator(attribute, negated) };
}

// Parses an unwrapped attribute-shaped envelope into filter state. Call only
// after the surface's support guard — throws on other shapes. Under dryad's
// plugin contract a throw escaping a plugin's `parse` is a plugin bug, so
// plugins gate on their guard and decline with `null` before calling this.
export function parseAttributeEnvelope(json: unknown, key: string): AttributeFilterState {
  const unwrapped = unwrapAttributeEnvelope(json, key);
  if (unwrapped == null) {
    throw new Error(`Not a ${key} filter: ${JSON.stringify(json)}`);
  }
  const { attribute, negated } = unwrapped;
  const operator = uiOperator(attribute, negated);
  return {
    field: String(attribute.field ?? ''),
    operator,
    value: isUnaryFilterOperator(operator) || attribute.value == null ? null : String(attribute.value),
    value2: isTwoValueFilterOperator(operator) && attribute.value2 != null ? String(attribute.value2) : null,
  };
}

// True when the wire node is an attribute filter with an operator the
// caller's surface declares. Deliberately no string requirement on the
// values: the Ember app's guard for this dialect
// (isSupportedAttributeFilter in app/utils/dryad/attribute-filter.ts) claims
// numeric values too and its parse coerces them with String(), so declining
// them here would strand on an error node a leaf the Ember app still edits.
// The device_attribute dialect differs on purpose — see
// device-attribute-plugin.ts.
export function isSupportedAttributeFilter(
  json: unknown,
  families: readonly GroupableFamily[],
  allowOneOfOperators = false,
): boolean {
  const unwrapped = unwrapSupportableAttributeEnvelope(json, 'attribute');
  if (unwrapped == null) {
    return false;
  }
  if (unwrapped.attribute.value2 != null && !isTwoValueFilterOperator(unwrapped.operator)) {
    return false;
  }
  return isSupportedOperator(unwrapped.operator, families, allowOneOfOperators);
}

// Parses a supported wire node into filter state. Call only after
// isSupportedAttributeFilter.
export function parseAttributeFilter(json: unknown): AttributeFilterState {
  return parseAttributeEnvelope(json, 'attribute');
}

// Serializes filter state to the wire shape: `{ attribute: {...} }`, with '!'
// operators becoming a `{ not: ... }` wrapper, unary operators dropping their
// value, and between operators carrying value2.
//
// The trims are this dialect's contract, not tidiness: Ember's
// serializeAttributeFilter (app/utils/dryad/attribute-filter.ts) trims
// field/value/value2 for the `{ attribute }` leaf, so a claimed leaf holding
// padded (or, via parse's String coercion, numeric) stored values rewrites on
// the next save in both apps identically. The device_attribute leaf's
// serializer never trims — per-dialect in Ember and here alike. Pinned by the
// 'attribute value normalization' fixtures in people-filter-format.test.ts.
export function serializeAttributeFilter(state: AttributeFilterState): AttributeFilter | { not: AttributeFilter } {
  const negated = state.operator.startsWith('!');
  const wireOperator = negated ? state.operator.slice(1) : state.operator;

  const filter: AttributeFilter = {
    attribute: {
      field: state.field.trim(),
      operator: wireOperator,
    },
  };
  if (!isUnaryFilterOperator(state.operator) && state.value != null) {
    filter.attribute.value = state.value.trim();
  }
  if (isTwoValueFilterOperator(state.operator) && state.value2 != null) {
    filter.attribute.value2 = state.value2.trim();
  }

  if (negated) {
    return { not: filter };
  }
  return filter;
}

// A one_of value is a marshalled JSON array of strings; "[]", a list of blanks
// and malformed JSON all hold no usable entries.
function isOneOfValueBlank(value: string | null): boolean {
  if (value == null || value === '') {
    return true;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return true;
  }
  if (!Array.isArray(parsed)) {
    return true;
  }
  return parsed.every((entry) => String(entry).trim() === '');
}

// Mirrors the backend cap (filters.MaxOneOfValues): lists larger than this are
// rejected at create time.
const ONE_OF_MAX_VALUES = 50;

function oneOfEntryCount(value: string | null): number {
  if (value == null || value === '') {
    return 0;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return 0;
  }
  return Array.isArray(parsed) ? parsed.length : 0;
}

/**
 * The list-cap and disallowed-field rules for one_of; empty for every other
 * operator. The field-rule message asks for a different operator, so it's
 * tagged to the operator input rather than the attribute one.
 */
export function oneOfFilterErrors(state: AttributeFilterState): Required<ValidationIssue>[] {
  const errors: Required<ValidationIssue>[] = [];
  if (!isOneOfOperator(state.operator)) {
    return errors;
  }
  if (oneOfEntryCount(state.value) > ONE_OF_MAX_VALUES) {
    errors.push({ message: `Use at most ${ONE_OF_MAX_VALUES} values.`, field: 'value' });
  }
  const name = state.field.trim();
  if (ONE_OF_DISALLOWED_FIELDS.has(name)) {
    errors.push({
      message: `The 'is one of' operator isn't supported for ${name}. Choose a different operator.`,
      field: 'operator',
    });
  }
  return errors;
}

/**
 * Only reachable by choosing the operator first and changing the attribute
 * after — the dropdown disables the email operators off the email field.
 * Untrimmed, matching the dropdown's enablement rule: a padded '  email  ' is
 * a different attribute, so the operator can't apply.
 */
export function emailFieldError(operator: string, field: string): string | undefined {
  if (!isEmailOperator(operator) || field === 'email') {
    return undefined;
  }
  return 'This operator can only be used for the email attribute';
}

/**
 * A blank value blocks the commit on an editing surface. one_of holds its
 * entries as a marshalled list, so its blankness is the list's.
 */
export function hasBlankValue(state: AttributeFilterState): boolean {
  if (isOneOfOperator(state.operator)) {
    return isOneOfValueBlank(state.value);
  }
  return state.value == null || state.value.trim() === '';
}

// The backend uses the same bounds for the "is a timestamp" condition.
const MIN_UNIX_TIMESTAMP = 99_999_999;
const MAX_UNIX_TIMESTAMP = 10_000_000_000;

function isValidTimestamp(timestamp: string): boolean {
  if (/\D/.test(timestamp)) {
    return false;
  }
  const value = Number(timestamp);
  return Number.isInteger(value) && value > MIN_UNIX_TIMESTAMP && value < MAX_UNIX_TIMESTAMP;
}

/**
 * A timestamp inequality needs a signed-integer value (a seconds offset or an
 * epoch). Reachable only from stored data — the sub-editor can't write
 * garbage — but a claimed leaf must surface the problem instead of refusing to
 * commit silently. A relative offset is any signed integer, while an absolute
 * value — including both ends of timestamp_bw — must be a plausible unix
 * epoch. Blank passes both rules — the blank-value check owns that message.
 */
const TIMESTAMP_VALUE_RULES = {
  days: { isValid: (value: string) => /^(-?\d+)?$/.test(value), message: 'Enter a number of days' },
  epoch: { isValid: (value: string) => value === '' || isValidTimestamp(value), message: 'Choose a date' },
} as const;

function timestampValueKind(operator: string): keyof typeof TIMESTAMP_VALUE_RULES | undefined {
  const variant = timestampVariantOf(operator);
  if (variant != null) {
    return variant === 'relative' ? 'days' : 'epoch';
  }
  return operator === 'timestamp_bw' ? 'epoch' : undefined;
}

export function timestampValueError(operator: string, value: string | null): string | undefined {
  const kind = timestampValueKind(operator);
  if (kind == null || TIMESTAMP_VALUE_RULES[kind].isValid((value ?? '').trim())) {
    return undefined;
  }
  return TIMESTAMP_VALUE_RULES[kind].message;
}

// Shape-only validation shared by the plugins; no external data lookups.
// Every issue is tagged with the state property it concerns (the email/one_of
// field rules with 'operator', whose message names the fix), so a row can put
// each message on the matching input via dryad's fieldError instead of only
// counting them.
// fallow-ignore-next-line complexity -- independent per-field checks; splitting adds indirection
export function validateAttributeFilter(state: AttributeFilterState): Required<ValidationIssue>[] {
  const errors: Required<ValidationIssue>[] = [];
  if (state.field.trim() === '') {
    errors.push({ message: 'Select an attribute', field: 'field' });
  }
  if (!isUnaryFilterOperator(state.operator) && hasBlankValue(state)) {
    errors.push({ message: 'Enter a value', field: 'value' });
  }
  const timestampError = timestampValueError(state.operator, state.value);
  if (timestampError != null) {
    errors.push({ message: timestampError, field: 'value' });
  }
  const timestamp2Error = timestampValueError(state.operator, state.value2);
  if (timestamp2Error != null) {
    errors.push({ message: timestamp2Error, field: 'value2' });
  }
  if (isTwoValueFilterOperator(state.operator) && (state.value2 == null || state.value2.trim() === '')) {
    errors.push({ message: 'Enter a second value', field: 'value2' });
  }
  const emailError = emailFieldError(state.operator, state.field);
  if (emailError != null) {
    errors.push({ message: emailError, field: 'operator' });
  }
  errors.push(...oneOfFilterErrors(state));
  return errors;
}
